import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import TextEdit from "../../molecules/TextEdit";
import TextArea from "../../molecules/TextArea";
import SelectTextField from "../../molecules/SelectTextField";
import CurrencyInput from "../../molecules/CurrencyInput";
import DayPicker from "../../molecules/DayPicker";
import NumberEdit from "../../molecules/NumberEdit";
import PageForm from "../../templates/PageForm";
import useProvinces from "../../../hooks/useProvinces";
import useCities from "../../../hooks/useCities";
import { NominalValue } from "../../../utils/NominalValue";
import { FaMapPin, FaLeaf, FaMapLocationDot, FaMap } from "react-icons/fa6";
import { IoBagOutline } from "react-icons/io5";
import { TbWeight } from "react-icons/tb";


const API_URL = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_URL;
const API_KEY = process.env.REACT_APP_CONTRACTS_MANAGEMENT_API_KEY;
const BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL;
const CONTRACTS_LIST_URL = '/contracts/0/100/id/ASC/s=;';

const API_KEY_PERSON = process.env.REACT_APP_API_KEY;
const PERSON_SERVICE_URL = process.env.REACT_APP_PERSON_API_URL;

type AggregationUnitMeasureDTO = {
    plural: string;
    sigla: string;
    singular: string;
}

type CropType = {
    id: number;
    name: string;
    referency: number;
    description: string;
    qualityStandard: string;
    aggregationUnitMeasureDTO?: AggregationUnitMeasureDTO;
    commodityUnitMeasure?: string;
    cropInformationRegister?: string;
    packagingFormCerc?: string;
    UnitOfMeasurementCerc?: string;
}

interface Participant {
    id: string;
    person_id: string;
    participant_type: 'BORROWER' | 'GUARANTOR' | 'ASSIGNOR' | 'GUARANTOR-RETAIL';
    name: string;
    document: string;
}

interface CPRFFormData {
    type: string;
    operation_number: string;
    sequencial_number: number;
    nominal_value: number;
    approved_value: string;
    interest_rate: string;
    expiration_date: string;
    operation_dictionary: {
        type: string;
        nominal_value: number;
        requested_value: number;
        interest_rate: number;
        expiration_date: string;
        status: number;
        measures: {
            referency: number;
            aggregation_unit_measure: string;
            amount_in_aggregation_unit: number;
            unit_measure: string;

        };
        commodity: {
            harvest_year_reference: string;
            price: number;
            quality?: string;
            description?: string;
            amount?: number;
            quotation: {
                date: string;
                source: string;
                city: string;
                province_name: string;
                province_initials: string;
            };
        };
        crop_type: {
            id: number;
            name: string;
        };
    }
}

const OperationLoanFormPage = () => {
    const navigate = useNavigate();
    const params = useParams();
    const credit_operation_id = params.credit_operation_id || params.id;
    const { fetchProvinces } = useProvinces();
    const { fetchCities } = useCities();
    const [isLoading, setIsLoading] = useState(false);
    const [cities, setCities] = useState<Array<{ value: string, label: string }>>([]);
    const [provinces, setProvinces] = useState<Array<{ value: string, label: string, initials: string }>>([]);
    const [cropTypes, setCropTypes] = useState<Array<{ value: string, label: string }>>([]);
    const [searchResults, setSearchResults] = useState<Array<{ value: string, label: string }>>([]);
    const [selectedParticipants, setSelectedParticipants] = useState<Participant[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const [selectedPerson, setSelectedPerson] = useState('');
    const [selectedParticipantType, setSelectedParticipantType] = useState('');
    const [CONTRACT_TEMPLATE_ID, setCONTRACT_TEMPLATE_ID] = useState<number | null>(null);
    let [selectCropType, setSelectCropType] = useState<CropType>();
    let [cropTypeId, setCropTypeId] = useState(0);

    const getCurrentDate = (): string => {
        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const year = today.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatHarvestYear = (value: string): string => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 2) return numbers;
        return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}`;
    };

    const handleHarvestYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatHarvestYear(e.target.value);
        setFormData({
            ...formData,
            operation_dictionary: {
                ...formData.operation_dictionary,
                commodity: { ...formData.operation_dictionary.commodity, harvest_year_reference: formatted }
            }
        });
    };

    const [formData, setFormData] = useState<CPRFFormData>({
        type: 'CPRF',
        operation_number: '',
        sequencial_number: 0,
        nominal_value: 0,
        approved_value: '',
        interest_rate: '',
        expiration_date: '',
        operation_dictionary: {
            type: 'CPRF',
            nominal_value: 0,
            requested_value: 0,
            interest_rate: 0,
            expiration_date: '',
            status: 1,
            measures: {
                aggregation_unit_measure: '',
                amount_in_aggregation_unit: 0,
                referency: 0,
                unit_measure: ''
            },
            commodity: {
                harvest_year_reference: '',
                price: 0,
                quality: '',
                description: '',
                amount: 0,
                quotation: {
                    date: getCurrentDate(),
                    source: '',
                    city: '',
                    province_name: '',
                    province_initials: ''
                }
            },
            crop_type: {
                id: 0,
                name: ''
            }
        }
    });

    const onQuotationValueChange = (value: number) => {
        const commodityBagAmount = formData.nominal_value / value;
        const commodityAmount = commodityBagAmount * formData.operation_dictionary.measures.referency;
        setFormData({
            ...formData, operation_dictionary: {
                ...formData.operation_dictionary,
                measures: {
                    ...formData.operation_dictionary.measures,
                    amount_in_aggregation_unit: commodityBagAmount,
                },
                commodity: {
                    ...formData.operation_dictionary.commodity,
                    amount: commodityAmount
                }
            }
        });
        // formData.operation_dictionary.measures.amount_in_aggregation_unit = commodityAmount;
    }

    const fetchContractData = async () => {
        if (!credit_operation_id || !API_URL || !API_KEY) {
            return null;
        }

        try {
            setIsLoading(true);
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const response = await fetch(`${API_URL}/contracts/loan/${credit_operation_id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || ''
                }
            });

            if (response.ok) {
                const data = await response.json();
                const schema = data.operation_dictionary || {};
                console.log('Fetched contract data:', data);

                // Format date from ISO to DD/MM/YYYY
                const formatDate = (isoDate: string) => {
                    if (!isoDate) return getCurrentDate();
                    const date = new Date(isoDate);
                    return date.toLocaleDateString('pt-BR');
                };

                const newFormData = {
                    type: schema.type || 'CPRF',
                    operation_number: data.operation_number || '',
                    sequencial_number: data.sequencial_number || 0,
                    nominal_value: data.nominal_value || 0,
                    approved_value: data.approved_value?.toString() || '',
                    interest_rate: '',
                    expiration_date: data.expiration_date ? formatDate(data.expiration_date) : '',
                    operation_dictionary: {
                        type: schema.type || 'CPRF',
                        nominal_value: schema.nominal_value || 0,
                        requested_value: schema.requested_value || 0,
                        interest_rate: schema.interest_rate || 0,
                        expiration_date: data.expiration_date ? formatDate(data.expiration_date) : '',
                        status: schema.status || 1,
                        measures: {
                            referency: schema.measures?.referency || 0,
                            aggregation_unit_measure: schema.measures?.aggregation_unit_measure || '',
                            amount_in_aggregation_unit: schema.measures?.amount_in_aggregation_unit || 0,
                            unit_measure: schema.measures?.unit_measure || ''
                        },
                        commodity: {
                            harvest_year_reference: schema.commodity?.harvest_year_reference || '',
                            price: schema.commodity?.price || 0,
                            quality: schema.commodity?.quality || '',
                            description: schema.commodity?.description || '',
                            amount: schema.commodity?.amount || 0,
                            quotation: {
                                date: schema.commodity?.quotation?.date ? formatDate(schema.commodity?.quotation?.date) : getCurrentDate(),
                                source: schema.commodity?.quotation?.source || '',
                                city: schema.commodity?.quotation?.city || '',
                                province_name: schema.commodity?.quotation?.province_name || '',
                                province_initials: schema.commodity?.quotation?.province_initials || ''
                            }
                        },
                        crop_type: {
                            id: schema.crop_type?.id || 0,
                            name: schema.crop_type?.name || ''
                        }
                    }
                };

                setFormData(newFormData);



                // Set crop type ID for dependent fields
                if (schema.crop_type?.id) {
                    setCropTypeId(schema.crop_type.id);
                }

                // Load participants
                if (data.participants && data.participants.length > 0) {
                    const participants = data.participants.map((p: any) => ({
                        id: p.id?.toString() || Date.now().toString(),
                        person_id: p.person_id || '',
                        participant_type: p.participant_type_name || p.participant_type,
                        name: p.name || 'Nome não informado',
                        document: p.document || ''
                    }));
                    setSelectedParticipants(participants);
                }

                return { schema, data };
            } else {
                toast.error('Erro ao carregar dados da operação');
                return null;
            }
        } catch (error) {
            console.error('Erro ao buscar operação:', error);
            toast.error('Erro ao conectar com o servidor');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Buscar províncias
                const provincesData = await fetchProvinces();
                const provinceItems = provincesData.map((item: any) => ({
                    value: item.id,
                    label: item.name,
                    initials: item.initials
                }));
                setProvinces(provinceItems);

                // Buscar tipos de cultura
                const cropTypesResponse = await fetch(`${BACKOFFICE_URL}/services/croptypes/all`);
                if (cropTypesResponse.ok) {
                    const cropTypesData = await cropTypesResponse.json();
                    const cropTypeItems = cropTypesData.map((item: any) => ({
                        value: item.id.toString(),
                        label: item.name
                    }));
                    setCropTypes(cropTypeItems);
                }

                // Buscar template de contrato
                const templateResponse = await fetch(`${BACKOFFICE_URL}/services/contract-template/loan`);
                if (templateResponse.ok) {
                    const templateData = await templateResponse.json();
                    setCONTRACT_TEMPLATE_ID(templateData.id);
                }

                // Load contract data if editing
                if (credit_operation_id) {
                    const contractData = await fetchContractData();

                    // Load cities after provinces are loaded and contract data is available
                    if (contractData?.schema?.commodity?.quotation?.province_initials) {
                        await fetchCitiesWrapper(contractData.schema.commodity.quotation.province_initials);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };
        fetchData();
    }, [credit_operation_id]);

    const validateForm = (): boolean => {
        if (!formData.nominal_value) {
            toast.error('Valor Nominal é obrigatório');
            return false;
        }
        if (selectedParticipants.length === 0) {
            toast.error('Pelo menos um participante é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.commodity.harvest_year_reference) {
            toast.error('Ano de Safra é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.commodity.price) {
            toast.error('Preço é obrigatório');
            return false;
        }
        if (!formData.operation_dictionary.measures.amount_in_aggregation_unit) {
            toast.error('Quantidade é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.date) {
            toast.error('Data da Cotação é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.source) {
            toast.error('Fonte da Cotação é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.city) {
            toast.error('Cidade é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.commodity.quotation.province_name) {
            toast.error('Província é obrigatória');
            return false;
        }
        if (!formData.operation_dictionary.crop_type.id) {
            toast.error('Tipo de Cultura é obrigatório');
            return false;
        }
        return true;
    };

    const fetchSelectedCropType = async () => {
        try {
            const response = await fetch(`${BACKOFFICE_URL}/services/croptypes/${cropTypeId}`);
            if (!response.ok) {
                throw new Error(`Erro na requisição: ${response.statusText}`);
            }
            const data: CropType = await response.json();
            setSelectCropType(data);
            setFormData(prev => ({
                ...prev,
                operation_dictionary: {
                    ...prev.operation_dictionary,
                    measures: {
                        ...prev.operation_dictionary.measures,
                        aggregation_unit_measure: data.aggregationUnitMeasureDTO?.plural || '',
                        unit_measure: data.commodityUnitMeasure || ''
                    },
                    commodity: {
                        ...prev.operation_dictionary.commodity,
                        description: data.description || '',
                        quality: data.qualityStandard || ''
                    }
                }
            }));
        } catch (err: any) {
            console.log(err.message || 'Erro desconhecido');
        } finally {
            // console.log(false);
        }
    };

    useEffect(() => {
        if (cropTypeId > 0) {
            fetchSelectedCropType();
        }
    }, [cropTypeId]);

    useEffect(() => {
        // Execute calculation after form data is loaded
        if (formData.operation_dictionary.commodity.price > 0 && formData.nominal_value > 0) {
            onQuotationValueChange(formData.operation_dictionary.commodity.price);
        }
    }, [formData.operation_dictionary.commodity.price, formData.nominal_value]);


    const handleSubmit = async () => {
        if (!validateForm()) return;

        if (!API_URL || !API_KEY) {
            toast.error('Configuração da API não encontrada');
            return;
        }

        setIsLoading(true);

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            const payload = {
                type: formData.type,
                operation_type: 'LOAN',
                operation_number: formData.operation_number,
                sequencial_number: formData.sequencial_number,
                operation_schema_id: 1,
                approved_value: parseFloat(formData.approved_value) || 0,
                interest_rate: formData.interest_rate,
                expiration_date: formData.expiration_date,
                nominal_value: formData.nominal_value,

                contract_built_status: 1,
                register_status: null,
                contract_template_id: CONTRACT_TEMPLATE_ID,

                participants: selectedParticipants.map(participant => ({
                    person_id: participant.person_id,
                    participant_type: participant.participant_type,
                    is_primary: true,
                    name: participant.name,
                    document: participant.document,
                })),
                operation_dictionary: formData.operation_dictionary
            };

            const url = credit_operation_id
                ? `${API_URL}/contracts/loan/save`
                : `${API_URL}/contracts/loan/save`;

            const payload_with_id = credit_operation_id
                ? { ...payload, credit_operation_id }
                : payload;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || ''
                },
                body: JSON.stringify(payload_with_id)
            });

            if (response.ok) {
                const message = credit_operation_id
                    ? 'Operação de empréstimo CPRF atualizada com sucesso!'
                    : 'Operação de empréstimo CPRF criada com sucesso!';
                toast.success(message);
                navigate(-1);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao salvar operação');
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('Erro ao conectar com o servidor');
        } finally {
            setIsLoading(false);
        }
    };

    const handlePersonSearch = async (searchValue: string) => {
        setSearchValue(searchValue);
        if (searchValue.length >= 2) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());
                const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${searchValue}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY_PERSON || '',
                        'Content-Type': 'application/json'
                    }
                });
                if (response.ok) {
                    const data = await response.json();
                    const personItems = data.map((item: any) => {
                        const cnpjDoc = item.documents?.find((doc: any) => doc.type === 'CNPJ');
                        const cpfDoc = item.documents?.find((doc: any) => doc.type === 'CPF');
                        const document = cnpjDoc?.number || cpfDoc?.number || '';
                        return {
                            value: item.uuid,
                            label: `${document} - ${item.name}`
                        };
                    });
                    setSearchResults(personItems);
                }
            } catch (error) {
                console.error('Erro ao buscar pessoa:', error);
            }
        } else {
            setSearchResults([]);
        }
    };

    const participantTypes = [
        { value: 'BORROWER', label: 'Tomador do crédito' },
        { value: 'GUARANTOR', label: 'Avalista/Fiador' },
        { value: 'GUARANTOR-RETAIL', label: 'Avalista/Fiador revenda parceira' },
        { value: 'ASSIGNOR', label: 'Cedente' }
    ];

    const addParticipant = () => {
        if (!selectedPerson || !selectedParticipantType) {
            toast.error('Selecione uma pessoa e um tipo de participante');
            return;
        }

        const existingParticipant = selectedParticipants.find(p => p.person_id === selectedPerson);
        if (existingParticipant) {
            toast.error('Esta pessoa já foi adicionada como participante');
            return;
        }

        const selectedPersonData = searchResults.find(p => p.value === selectedPerson);
        if (!selectedPersonData) return;

        const [document, name] = selectedPersonData.label.split(' - ');
        const newParticipant: Participant = {
            id: Date.now().toString(),
            person_id: selectedPerson,
            participant_type: selectedParticipantType as 'BORROWER' | 'GUARANTOR' | 'ASSIGNOR' | 'GUARANTOR-RETAIL',
            name: name || '',
            document: document || ''
        };

        setSelectedParticipants([...selectedParticipants, newParticipant]);
        setSelectedPerson('');
        setSelectedParticipantType('');
        setSearchValue('');
        setSearchResults([]);
    };

    const removeParticipant = (id: string) => {
        setSelectedParticipants(selectedParticipants.filter(p => p.id !== id));
    };

    const handleProvinceChange = async (provinceId: string) => {
        if (provinceId) {
            const selectedProvince = provinces.find(p => p.value == provinceId) || null;
            if (selectedProvince) {
                await fetchCitiesWrapper(selectedProvince.initials);
            }
            setFormData({
                ...formData,
                operation_dictionary: {
                    ...formData.operation_dictionary,
                    commodity: {
                        ...formData.operation_dictionary.commodity,
                        quotation: {
                            ...formData.operation_dictionary.commodity.quotation,
                            city: '',
                            province_name: selectedProvince?.label || '',
                            province_initials: selectedProvince?.initials || ''
                        }
                    }
                }
            });
        } else {
            setCities([]);
            setFormData({
                ...formData,
                operation_dictionary: {
                    ...formData.operation_dictionary,
                    commodity: {
                        ...formData.operation_dictionary.commodity,
                        quotation: {
                            ...formData.operation_dictionary.commodity.quotation,
                            city: '',
                            province_name: '',
                            province_initials: ''
                        }
                    }
                }
            });
        }
    };

    const fetchCitiesWrapper = async (initials: string) => {
        try {
            setIsLoading(true);
            if (initials) {
                const citiesData = await fetchCities(initials);
                const cityItems = citiesData.map((item: any) => ({
                    value: item.id,
                    label: item.name
                }));
                setCities(cityItems);

            }
        } catch (error) {
            console.error('Erro ao buscar cidades:', error);
            toast.error('Erro ao carregar cidades');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageForm
            pageTitle="Nova Operação de Empréstimo CPRF"
            apiVersion=""
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Contratos', url: CONTRACTS_LIST_URL },
                { label: 'Nova operação de empréstimo CPRF', url: '#' }
            ]}
            controlSlot={(
                <div className="pt-4 flex justify-end">
                    <button
                        onClick={() => navigate(CONTRACTS_LIST_URL)}
                        disabled={isLoading}
                        className="text-white bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded text-sm px-5 py-2.5 text-center mr-2 mb-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            )}
        >
            <div className="container mx-auto px-2 py-4">
                <h3 className="text-lg font-semibold mb-4">Dados da Operação</h3>
                <div className="mb-6">
                    <div className="text-lg font-medium">
                        <span className="text-gray-600">Tipo de Operação:</span> Empréstimo CPRF
                    </div>
                </div>

                {/* Número do Contrato e Código de Operação */}
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                    <div className="md:col-span-2">
                        <TextEdit
                            label="Número do Contrato"
                            value={formData.sequencial_number.toString()}
                            onChange={() => { }}
                            placeholder=""
                            disabled
                        />
                    </div>
                    <div className="md:col-span-2">
                        <TextEdit
                            label="Código de Operação"
                            value={formData.operation_number}
                            onChange={(e) => setFormData({ ...formData, operation_number: e.target.value })}
                            maxLength={8}
                            placeholder=""
                        />
                    </div>
                </div>

                {/* Participantes */}
                <h4 className="text-md font-semibold mb-4">Participantes</h4>
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-4">
                    <div className="md:col-span-2">
                        <TextEdit
                            label="Buscar Pessoa"
                            placeholder=""
                            value={searchValue}
                            onChange={(e) => handlePersonSearch(e.target.value)}
                        />
                    </div>
                    <div className="md:col-span-5">
                        <SelectTextField
                            label="Pessoa"
                            value={selectedPerson}
                            items={searchResults}
                            onChange={(e) => setSelectedPerson(e.target.value)}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <SelectTextField
                            label="Tipo de Participante"
                            value={selectedParticipantType}
                            items={participantTypes}
                            onChange={(e) => setSelectedParticipantType(e.target.value)}
                        />
                    </div>
                    <div className="md:col-span-1 flex items-end">
                        <button
                            type="button"
                            onClick={addParticipant}
                            className="w-full text-white bg-gradient-to-r from-green-500 via-green-600 to-green-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded text-sm px-4 py-2.5 text-center"
                        >
                            Adicionar
                        </button>
                    </div>
                </div>

                {/* Tabela de Participantes */}
                <div className="mb-6">
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border border-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Documento</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {selectedParticipants.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                                            Nenhum participante adicionado
                                        </td>
                                    </tr>
                                ) : (
                                    selectedParticipants.map((participant) => (
                                        <tr key={participant.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{participant.document}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{participant.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                {participantTypes.find(t => t.value === participant.participant_type)?.label}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                <button
                                                    onClick={() => removeParticipant(participant.id)}
                                                    className="text-red-600 hover:text-red-900"
                                                >
                                                    Remover
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Valores */}
                <h4 className="text-md font-semibold mb-4">Valores</h4>
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                    <div className="md:col-span-2">
                        <CurrencyInput
                            label="Valor Nominal *"
                            prefix="R$"
                            fractionDigits={2}
                            value={formData.nominal_value || 0}
                            onChange={(value) => setFormData({ ...formData, nominal_value: value })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <CurrencyInput
                            label="Valor Aprovado"
                            prefix="R$"
                            fractionDigits={2}
                            value={parseFloat(formData.approved_value) || 0}
                            onChange={(value) => setFormData({ ...formData, approved_value: value.toString() })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <CurrencyInput
                            label="Valor Solicitado"
                            prefix="R$"
                            fractionDigits={2}
                            value={formData.operation_dictionary.requested_value || 0}
                            onChange={(value) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    requested_value: value
                                }
                            })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <CurrencyInput
                            label="Taxa de remuneração"
                            prefix="%"
                            fractionDigits={2}
                            value={formData.operation_dictionary.interest_rate || 0}
                            onChange={(value) => setFormData({
                                ...formData,
                                interest_rate: value.toString(),
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    interest_rate: value
                                }
                            })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <DayPicker
                            label="Data de vencimento"
                            value={formData.operation_dictionary.expiration_date}
                            onChange={(value) => setFormData({
                                ...formData,
                                expiration_date: value,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    expiration_date: value
                                }
                            })}
                        />
                    </div>
                </div>

                {/* Cultura */}
                <h4 className="text-md font-semibold mb-4">Cultura</h4>
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                    <div className="md:col-span-6">
                        <SelectTextField
                            label="Tipo de Cultura *"
                            value={formData.operation_dictionary.crop_type.id.toString()}
                            items={cropTypes}
                            prefix={<FaLeaf className="text-xl" />}
                            onChange={(e) => {
                                const selectedCrop = cropTypes.find(crop => crop.value === e.target.value);
                                setFormData({
                                    ...formData,
                                    operation_dictionary: {
                                        ...formData.operation_dictionary,
                                        crop_type: {
                                            id: parseInt(e.target.value, 10),
                                            name: selectedCrop?.label || ''
                                        }
                                    }
                                });
                                setCropTypeId(parseInt(e.target.value, 10));
                            }}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-10 gap-4 mb-6">
                    <div className="md:col-span-2">
                        <TextEdit
                            label="Ano de Safra *"
                            value={formData.operation_dictionary.commodity.harvest_year_reference}
                            onChange={handleHarvestYearChange}
                            placeholder=""
                            maxLength={5}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <CurrencyInput
                            label="Valor da cotação *"
                            prefix="R$"
                            fractionDigits={2}
                            value={formData.operation_dictionary.commodity.price || 0}
                            onChange={(value) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: { ...formData.operation_dictionary.commodity, price: value }
                                }
                            })}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <DayPicker
                            label="Data da Cotação *"
                            value={formData.operation_dictionary.commodity.quotation.date}
                            onChange={(value) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: {
                                        ...formData.operation_dictionary.commodity,
                                        quotation: { ...formData.operation_dictionary.commodity.quotation, date: value }
                                    }
                                }
                            })}
                        />
                    </div>
                    <div className="md:col-span-4">
                        <TextEdit
                            label="Fonte da Cotação"
                            value={formData.operation_dictionary.commodity.quotation.source}
                            onChange={(e) => setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: {
                                        ...formData.operation_dictionary.commodity,
                                        quotation: { ...formData.operation_dictionary.commodity.quotation, source: e.target.value }
                                    }
                                }
                            })}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <SelectTextField
                        label="UF origem da cotação"
                        emptyLabel="..."
                        prefix={<FaMap className="text-xl" />}
                        value={formData.operation_dictionary.commodity.quotation.province_initials}
                        items={provinces.map(p => ({ value: p.initials, label: p.label }))}
                        onChange={(e) => {
                            const selectedProvince = provinces.find(p => p.initials === e.target.value);
                            if (selectedProvince) {
                                handleProvinceChange(selectedProvince.value);
                            }
                        }}
                    />
                    <SelectTextField
                        label="Cidade origem da cotação"
                        emptyLabel="..."
                        prefix={<FaMapLocationDot className="text-xl" />}
                        value={formData.operation_dictionary.commodity.quotation.city}
                        items={cities.map(c => ({ value: c.label, label: c.label }))}
                        isLoading={isLoading}
                        onChange={(e) => {
                            setFormData({
                                ...formData,
                                operation_dictionary: {
                                    ...formData.operation_dictionary,
                                    commodity: {
                                        ...formData.operation_dictionary.commodity,
                                        quotation: { ...formData.operation_dictionary.commodity.quotation, city: e.target.value }
                                    }
                                }
                            });
                        }}
                    />

                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <CurrencyInput
                        label={`Quantidade em ${selectCropType?.aggregationUnitMeasureDTO?.plural || ''}`}
                        fractionDigits={3}
                        id="amount-in-aggregation-unit"
                        value={formData.operation_dictionary.measures.amount_in_aggregation_unit || 0}
                        onChange={(value) => setFormData({
                            ...formData,
                            operation_dictionary: {
                                ...formData.operation_dictionary,
                                measures: { ...formData.operation_dictionary.measures, amount_in_aggregation_unit: value }
                            }
                        })}
                    />
                    <CurrencyInput
                        label={`Quantidade de ${selectCropType?.commodityUnitMeasure || ''} por ${selectCropType?.aggregationUnitMeasureDTO?.singular || ''}`}
                        fractionDigits={0}
                        value={formData.operation_dictionary.measures.referency || 0}
                        onChange={(value) => setFormData({
                            ...formData,
                            operation_dictionary: {
                                ...formData.operation_dictionary,
                                measures: { ...formData.operation_dictionary.measures, referency: value }
                            }
                        })}
                    />
                    <TextEdit
                        label={`Unidade de Medida`}
                        value={formData.operation_dictionary.measures.unit_measure || ''}
                        onChange={(e) => setFormData({
                            ...formData,
                            operation_dictionary: {
                                ...formData.operation_dictionary,
                                measures: { ...formData.operation_dictionary.measures, unit_measure: e.target.value }
                            }
                        })}
                    />
                </div>

                <div className="mb-6">
                    <TextArea
                        id="commodity-description"
                        rows={4}
                        label="Descrição do produto"
                        value={formData.operation_dictionary.commodity.description}

                        onChange={(e) => setFormData({
                            ...formData,
                            operation_dictionary: {
                                ...formData.operation_dictionary,
                                commodity: { ...formData.operation_dictionary.commodity, description: e.target.value }
                            }
                        })}
                    />
                </div>

                <div className="mb-6">
                    <TextArea
                        label="Qualidade"
                        value={formData.operation_dictionary.commodity.quality}
                        onChange={(e) => setFormData({
                            ...formData,
                            operation_dictionary: {
                                ...formData.operation_dictionary,
                                commodity: { ...formData.operation_dictionary.commodity, quality: e.target.value }
                            }
                        })}
                    />
                </div>

            </div>
        </PageForm>
    );
};

export default OperationLoanFormPage;